import EChartCommon from '@/pages/opinion/components/EChartCommon';
import { chartAllColors } from '@/pages/opinion/constants';
import { formatTextInfoSimpleFunc } from '@/pages/opinion/utils/chart';
import { DBO_V } from '@op/utils/format';
import { formatTimeToShow } from '@op/utils/time';
import React, { useMemo, useRef } from 'react';
import { getLocale } from 'umi';
import { formatChartParamsDate } from '@/pages/opinion/components/ChartCommon/formatter';
import { TopicLevelType } from '@op/pages/Summary/context';

interface IPieChartProps {
  data: Array<{ name: string; count: number; child?: string[]; }>;
  loading?: boolean;
  translationData?: any;
  formatMessage: (value: { id: string; }) => string;
  transTopic?: (value: string, level?: TopicLevelType) => string;
  height?: number;
  filter?: any;
  total: number;
}

export default function PieChart({
  data,
  loading,
  translationData,
  formatMessage,
  transTopic,
  height = 327,
  filter = {},
  total,
}: IPieChartProps) {
  const locale = getLocale();
  const eChartsRef = useRef(null as any);

  const translationFunc = useMemo(() => {
    if (transTopic) {
      return transTopic;
    }
    const func = formatTextInfoSimpleFunc(translationData, locale);

    return (value: string) => {
      const key = value?.toLocaleLowerCase();
      return func(key);
    };
  }, [translationData, locale, transTopic]);

  const options = useMemo(() => {
    const filteredData =
      data?.filter(
        (item) => !['others', 'other'].includes(item.name.toLocaleLowerCase()),
      ) || [];

    const pieData = filteredData.map((item, index) => {
      const color = chartAllColors[index % chartAllColors.length];
      return {
        name: item.name,
        value: item.count,
        itemStyle: { color },
      };
    });

    return {
      tooltip: {
        trigger: 'item',
        className: 'dbo-mentions-sentiment-box-tooltip-wrapper',
        enterable: true,
        formatter: (params: any) => {
          return formatChartParamsDate({
            title: formatTimeToShow(filter),
            params,
            listTitleFunc: (item) => translationFunc(item.name) || '',
            valueFormat: (value) => {
              const percent = DBO_V.percentage(params?.value, total, 2);
              return `${isNaN(value) ? '-' : DBO_V.reviews(value)} (${percent})`;
            },
          });
        },
      },
      series: [
        {
          type: 'pie',
          data: pieData,
          label: {
            color: '#262832',
            alignTo: 'edge',
            edgeDistance: 10,
            formatter: (params: any) => {
              const percent = DBO_V.percentage(params.value, total, 2);
              let percentText = DBO_V.reviews(params.value);
              if (params?.value) {
                percentText += `(${percent})`;
              }
              const topicName = translationFunc(params.name);
              return `{value| ${topicName}}: {a|${percentText}}\n{hrLine|}\n{a|}`;
            },
            rich: {
              a: {
                // color: '#999',
                lineHeight: 20, // 设置最后一行空数据高度，为了能让延长线与hr线对接起来
                align: 'center',
              },
              hrLine: {
                // 设置hr是为了让中间线能够自适应长度
                borderColor: 'auto', // hr的颜色为auto时候会主动显示颜色的
                width: '105%',
                borderWidth: 0.5,
                height: 0.5,
              },
              per: {
                // 用百分比数据来调整下数字位置，显的好看些。如果不设置，formatter最后一行的空数据就不需要
                padding: [4, 0],
              },
            },
          },
          labelLayout: (params: any) => {
            const isLeft =
              params.labelRect.x <
              eChartsRef?.current?.getEchartsInstance?.()?.getWidth() / 2;
            const points = params.labelLinePoints;
            // Update the end point.
            points[2][0] = isLeft
              ? params.labelRect.x
              : params.labelRect.x + params.labelRect.width;
            return {
              labelLinePoints: points,
            };
          },
          emphasis: {
            focus: 'self',
            label: {
              opacity: 0.2,
            },
            labelLine: {
              lineStyle: {
                opacity: 0.2,
              },
            },
          },
          blur: {
            label: { opacity: 0.2 },
            itemStyle: {
              opacity: 0.2,
            },
            labelLine: {
              lineStyle: {
                opacity: 0.2,
              },
            },
          },
          avoidLabelOverlap: true,
          labelLine: {
            show: true,
            length: 15,
            length2: 80,
            smooth: 0.2,
            minTurnAngle: 90,
          },
        },
      ],
      legend: {
        data: pieData.map((item: { name: any; itemStyle: any; }) => ({
          name: item.name,
          itemStyle: item.itemStyle,
        })),
        formatter: (name: string) => {
          return translationFunc(name);
        },
        left: 'center',
        type: 'scroll',
      },
    };
  }, [data, translationFunc, formatMessage, total, filter]);

  return (
    <EChartCommon
      options={options}
      height={height}
      empty={data?.length === 0}
      loading={loading}
      optionsProps={{ type: 'pie', pieLabelAlignTo: 'edge' }}
      ref={eChartsRef}
    />
  );
}
